/**
 * Component: Product gallery
 * ------------------------------------------------------------------------------
 * An Product gallery file that contains scripts to function.
 * Zoom functionality is using Drift.
 * https://github.com/imgix/drift#basic-usage
 *
 * @namespace productGallery
 */
import log from 'salvo-lite/log'

import Drift from 'drift-zoom'
import debounce from 'lodash-es/debounce'

import cssClasses from '../helpers/cssClasses'
import bind from '../mixins/bind'
import breakpoints from '../helpers/breakpoints'
import {on} from '../helpers/utils'

class ProductGallery {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            mainImageSlider: '[js-product-gallery="main-slider"]',
            mainImageSlide: '[js-product-gallery="main-slide"]',
            mainVideoSlide: '[js-product-gallery="video-slide"]',
            thumbnailSlider: '[js-product-gallery="thumbnail-slider"]',
            thumbnailSlide: '[js-product-gallery="thumbnail-slide"]',
            zoomPane: '[js-product-gallery="zoom-pane"]',
            runFlickity: '[js-run-product-gallery]',
            ...JSON.parse(elem.getAttribute('data-product-gallery') || '{}')
        }

        /**
         * Node selectors.
         */
        this._mainImageSlide = document.querySelectorAll(this._settings.mainImageSlide)
        this._mainVideoSlide = document.querySelectorAll(this._settings.mainVideoSlide)
        this._thumbnailSlide = document.querySelectorAll(this._settings.thumbnailSlide)
        this._zoomPane = document.querySelector(this._settings.zoomPane)
        this._runFlickity = document.querySelector(this._settings.runFlickity)

        /**
         * Options for Drift zoom.
         */
        this._zoomOptions = {
            hoverBoundingBox: false,
            touchBoundingBox: true,
            paneContainer: this._zoomPane,
            touchDelay: 500,
            zoomFactor: 2
        }

        /**
         * Instance properties.
         */
        this._featuredImages = {}

        /**
         * Initialise component bind.
         */
        this._binder = bind(this._elem)

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setDefaultState()
        this._setResizeEvents()
        this._setEventBusListeners()

        if (this._thumbnailSlide.length > 1) {
            this._mainImageSlider = this._theme.getComponentsById('product-image-slider')._carousel
            this._thumbnailSlider = this._theme.getComponentsById('product-image-slider-thumbs')._carousel
        }
    }

    /**
     * Sets the EventBus listeners.
     */
    _setEventBusListeners () {
        window.Frame.EventBus.listen('Variant:change', (variant) => {
            if (!variant) {
                return
            }

            if (!this._isVariantImageAvailable(variant.id)) {
                return
            }

            const selectedVariantImage = document.querySelector(
                `${this._settings.thumbnailSlide}[data-variant-id="${variant.id}"]`
            )

            const id = selectedVariantImage.dataset.id

            this._mainImageSlider.select(id)
            this._thumbnailSlider.select(id)
        })
    }

    /**
     * Check if variant image is available.
     * @param {Number} id - Variant id of selected option.
     * @returns {Boolean}
     */
    _isVariantImageAvailable (id) {
        return Boolean(
            document.querySelector(`${this._settings.thumbnailSlide}[data-variant-id="${id}"]`)
        )
    }

    /**
     * Set window resize events for responsive initialisation.
     */
    _setResizeEvents () {
        on('resize', debounce(this._handleResizeEvent, 250))
    }

    /**
     * Set default state based on screen size.
     */
    _setDefaultState () {
        if (!window.matchMedia(`(min-width: ${breakpoints.small})`).matches) {
            return
        }

        this._setZoomEvents()
        this._binder.set()
    }

    /**
     * Check if Flickity should be enabled
     */
    _runFlickitySlider () {
        const runGallery = this._runFlickity.getAttribute('js-run-product-gallery')
        return runGallery
    }

    /**
     * Handle resize events through debounce callback.
     * - Disable zoom on small devices.
     */
    _handleResizeEvent () {
        if (!window.matchMedia(`(min-width: ${breakpoints.small})`).matches) {
            this._destroyZoomEvents()
            this._binder.remove()
            return
        }

        if (this._binder && !this._binder.isSet()) {
            this._setZoomEvents()
        }
    }

    /**
     * Set zoom events on featured Images and remove from Videos.
     */
    _setZoomEvents () {
        [...this._mainImageSlide].forEach((element, index) => {
            if (element.tagName.toLowerCase() === 'img') {
                this._featuredImages[index] = new Drift(element, this._zoomOptions)
            }
        })
    }

    /**
     * Remove zoom events on featured Images.
     */
    _destroyZoomEvents () {
        Object.keys(this._featuredImages).forEach((key) => {
            this._featuredImages[key].destroy()
        })
    }

    /**
     * Handles video iframes to autoplay and autostop.
     */
    _autoplayCheck () {
        [this._mainVideoSlide].forEach((item) => {
            const itemSrc = item.getAttribute('src')
            const newSrc = `${itemSrc}&autoplay=1`

            if (item.parentElement.classList.contains(cssClasses.selected)) {
                item.setAttribute('src', newSrc)
            } else {
                item.setAttribute('src', itemSrc.replace('&autoplay=1', ''))
            }
        })

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // })
    }
}

export default ProductGallery



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ProductGallery.js